import { Component, ElementRef, OnInit, OnDestroy, Renderer2, AfterViewInit, HostBinding } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { take, takeUntil, debounceTime } from 'rxjs/operators';
import { Subject, fromEvent } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DomSanitizer } from '@angular/platform-browser';
import { FiltersService } from '../../filters/filters.service';
import { NewService } from '../../news/new.service';
import { ModalService } from '../../../shared/services/modal.service';

interface Banner {
  image: string;
  text?: string;
}

interface Post {
  _id: string;
  title: string;
  description: string;
  photo: string;
  createAt?: string;
}

@Component({
  selector: 'app-new-index-home',
  templateUrl: './new-index-home.component.html',
  styleUrls: ['./new-index-home.component.scss']
})
export class NewIndexComponent implements OnInit, OnDestroy, AfterViewInit {
  @HostBinding('class.loaded') isLoaded = false;

  private destroy$ = new Subject<void>();
  private scrollObserver: IntersectionObserver;

  recentPosts: Post[] = [];
  user: any;
  qtdPost: number = 3;
  banners: Banner[] = [];

  constructor(
    private router: Router,
    private newService: NewService,
    private renderer: Renderer2,
    private element: ElementRef,
    private domSanitizer: DomSanitizer,
    private filtersService: FiltersService,
    private modalService: ModalService,
  ) { }

  ngOnInit(): void {
    this.setupPageStyles();
    this.loadAllBanners();
    this.getPosts();
    this.setupScrollToTop();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initScrollAnimations();
      this.initIntersectionObserver();
      this.isLoaded = true;
    }, 500);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
  }

  private setupPageStyles(): void {
    this.renderer.setStyle(
      this.element.nativeElement.offsetParent,
      'overflow',
      'visible'
    );
  }

  private setupScrollToTop(): void {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((evt) => {
        if (!(evt instanceof NavigationEnd)) {
          return;
        }
        window.scrollTo(0, 0);
      });
  }

  private initScrollAnimations(): void {
    fromEvent(window, 'scroll')
      .pipe(
        debounceTime(10),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.checkElementsInView();
      });
  }

  private initIntersectionObserver(): void {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    this.scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target as HTMLElement;
          const animationDelay = element.getAttribute('data-delay') || '0';

          setTimeout(() => {
            element.classList.add('visible');
          }, parseInt(animationDelay, 10));

          this.scrollObserver.unobserve(element);
        }
      });
    }, options);

    const animatedElements = this.element.nativeElement.querySelectorAll('.reveal-left, .reveal-right, .reveal-up');
    animatedElements.forEach(el => {
      this.scrollObserver.observe(el);
    });
  }

  private checkElementsInView(): void {
    const animatedElements = this.element.nativeElement.querySelectorAll('.reveal-left:not(.visible), .reveal-right:not(.visible), .reveal-up:not(.visible)');

    animatedElements.forEach(element => {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;

      if (rect.top <= windowHeight * 0.8) {
        const animationDelay = element.getAttribute('data-delay') || '0';

        setTimeout(() => {
          element.classList.add('visible');
        }, parseInt(animationDelay, 10));
      }
    });
  }

  async getPosts(): Promise<void> {
    try {
      const posts: any = await this.newService.getPosts(1)
        .pipe(take(1))
        .toPromise();

      if (posts.retorno) {
        this.recentPosts = posts.retorno.slice(0, this.qtdPost);
      }
    } catch (error) {
      this.handleError('Erro ao carregar posts', error);
    }
  }

  openPost(postId: string): void {
    this.router.navigate([`${environment.url_version}/blog-post/${postId}`]);
  }

  scroolTo(targetName: string): void {
    const element = document.getElementById(targetName);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  onNavigate(url: string): void {
    window.open(url, "_blank");
  }

  navigateTo(route: string): void {
    this.router.navigate([`${environment.url_version}/${route}`]);
  }

  trustedImg(photo: string): any {
    return this.domSanitizer.bypassSecurityTrustStyle(photo);
  }

  getDay(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.getDate().toString().padStart(2, '0');
  }

  getMonth(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
    return months[date.getMonth()];
  }

  loadAllBanners(): void {
    try {
      this.filtersService.getAllBanners()
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: any) => {
            if (response.status === 200) {
              this.banners = response.body.result.map((item: any) => ({
                image: `data:image/png;base64,${item.image}`
              }));
            } else {
              this.handleError('Falha!', response.body.msg);
            }
          },
          (error: any) => {
            this.handleError('Falha!', error.error.msg);
          }
        );
    } catch (error) {
      this.handleError('Falha!', 'Ocorreu um erro ao carregar banners.');
    }
  }

  private handleError(title: string, message: string | any): void {
    const errorMessage = typeof message === 'string'
      ? message
      : 'Algo deu errado. Por favor, tente novamente.';

    this.modalService.openModal(
      title,
      errorMessage,
      'error',
      'Ok'
    ).subscribe();
  }
}